import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { adminApi } from "../services/adminApi";
import Pagination from "../components/Pagination";

function profileToEditForm(profile) {
  const w = profile?.wallet;
  return {
    email: profile?.email || "",
    name: profile?.name || "",
    phone: profile?.phone || "",
    address: profile?.address || "",
    avatar: profile?.avatar || "",
    referralCode: profile?.referralCode || "",
    role: profile?.role || "user",
    isActive: Boolean(profile?.isActive),
    isEmailVerified: Boolean(profile?.isEmailVerified),
    nomineeName: profile?.nomineeName || "",
    nomineeEmail: profile?.nomineeEmail || "",
    nomineePhone: profile?.nomineePhone || "",
    nomineeAddress: profile?.nomineeAddress || "",
    walletName: profile?.walletName || "",
    withdrawalMethod: profile?.withdrawalMethod || "",
    destinationAddress: profile?.destinationAddress || "",
    totalEarnings:
      profile?.totalEarnings === undefined ? "" : String(profile.totalEarnings),
    totalWorkingIncome:
      profile?.totalWorkingIncome === undefined
        ? ""
        : String(profile.totalWorkingIncome),
    totalNonWorkingIncome:
      profile?.totalNonWorkingIncome === undefined
        ? ""
        : String(profile.totalNonWorkingIncome),
    initialInvestmentLevel:
      profile?.initialInvestmentLevel === undefined
        ? ""
        : String(profile.initialInvestmentLevel),
    binaryParentId: profile?.binaryParentId || "",
    binarySide: profile?.binarySide || "",
    twoFAEnabled: Boolean(profile?.twoFAEnabled),
    twoFAFailedAttempts:
      profile?.twoFAFailedAttempts === undefined ||
      profile?.twoFAFailedAttempts === null
        ? 0
        : profile.twoFAFailedAttempts,
    twoFALockedUntil: profile?.twoFALockedUntil
      ? new Date(profile.twoFALockedUntil).toISOString().slice(0, 16)
      : "",
    clearPendingOtp: false,
    newPassword: "",
    walletBalance: w?.balance === undefined ? "" : String(w.balance),
    walletMain: w?.mainBalance === undefined ? "" : String(w.mainBalance),
    walletProfit: w?.profitBalance === undefined ? "" : String(w.profitBalance),
    walletReward: w?.rewardBalance === undefined ? "" : String(w.rewardBalance),
  };
}

export default function UserDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [details, setDetails] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [txFilters, setTxFilters] = useState({ type: "", status: "" });
  const [editForm, setEditForm] = useState(() => profileToEditForm(null));

  const loadDetails = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const next = await adminApi.getUserDetails(userId, { page, pageSize });
      setDetails(next);
      setEditForm(profileToEditForm(next.profile));
      setError("");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load user details");
    } finally {
      setLoading(false);
    }
  }, [userId, page, pageSize]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  useEffect(() => {
    if (location.state?.openEdit) {
      setEditOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const setField = (key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
    setSaveMessage("");
  };

  const buildUpdatePayload = useMemo(
    () => () => {
      const e = editForm;
      const payload = {
        email: e.email.trim(),
        name: e.name.trim(),
        phone: e.phone.trim() === "" ? null : e.phone.trim(),
        address: e.address.trim() === "" ? null : e.address,
        avatar: e.avatar.trim() === "" ? null : e.avatar.trim(),
        referralCode: e.referralCode.trim(),
        role: e.role,
        isActive: e.isActive,
        isEmailVerified: e.isEmailVerified,
        nomineeName: e.nomineeName.trim() === "" ? null : e.nomineeName.trim(),
        nomineeEmail: e.nomineeEmail.trim() === "" ? null : e.nomineeEmail.trim(),
        nomineePhone: e.nomineePhone.trim() === "" ? null : e.nomineePhone.trim(),
        nomineeAddress:
          e.nomineeAddress.trim() === "" ? null : e.nomineeAddress.trim(),
        walletName: e.walletName.trim() === "" ? null : e.walletName.trim(),
        withdrawalMethod:
          e.withdrawalMethod.trim() === "" ? null : e.withdrawalMethod.trim(),
        destinationAddress:
          e.destinationAddress.trim() === ""
            ? null
            : e.destinationAddress.trim(),
        totalEarnings: Number(e.totalEarnings),
        totalWorkingIncome: Number(e.totalWorkingIncome),
        totalNonWorkingIncome: Number(e.totalNonWorkingIncome),
        initialInvestmentLevel: Number(e.initialInvestmentLevel),
        binaryParentId:
          e.binaryParentId.trim() === "" ? null : e.binaryParentId.trim(),
        binarySide: e.binarySide.trim() === "" ? null : e.binarySide.trim().toUpperCase(),
        twoFAEnabled: e.twoFAEnabled,
        twoFAFailedAttempts: parseInt(e.twoFAFailedAttempts, 10) || 0,
        twoFALockedUntil:
          e.twoFALockedUntil.trim() === ""
            ? null
            : new Date(e.twoFALockedUntil).toISOString(),
      };

      if (!Number.isFinite(payload.totalEarnings)) {
        throw new Error("Total earnings must be a valid number.");
      }
      if (!Number.isFinite(payload.totalWorkingIncome)) {
        throw new Error("Total working income must be a valid number.");
      }
      if (!Number.isFinite(payload.totalNonWorkingIncome)) {
        throw new Error("Total non-working income must be a valid number.");
      }
      if (!Number.isFinite(payload.initialInvestmentLevel)) {
        throw new Error("Initial investment level must be a valid number.");
      }

      const wallet = {};
      if (e.walletBalance !== "") {
        const n = Number(e.walletBalance);
        if (!Number.isFinite(n))
          throw new Error("Wallet total balance must be a number.");
        wallet.balance = n;
      }
      if (e.walletMain !== "") {
        const n = Number(e.walletMain);
        if (!Number.isFinite(n))
          throw new Error("Wallet main balance must be a number.");
        wallet.mainBalance = n;
      }
      if (e.walletProfit !== "") {
        const n = Number(e.walletProfit);
        if (!Number.isFinite(n))
          throw new Error("Wallet profit balance must be a number.");
        wallet.profitBalance = n;
      }
      if (e.walletReward !== "") {
        const n = Number(e.walletReward);
        if (!Number.isFinite(n))
          throw new Error("Wallet reward balance must be a number.");
        wallet.rewardBalance = n;
      }
      payload.wallet =
        Object.keys(wallet).length > 0 ? wallet : undefined;

      if (e.clearPendingOtp) {
        payload.clearPendingOtp = true;
      }

      if (e.newPassword && String(e.newPassword).length > 0) {
        payload.newPassword = String(e.newPassword);
      }

      return payload;
    },
    [editForm],
  );

  const handleSave = async () => {
    if (!userId) return;
    setSaveLoading(true);
    setSaveMessage("");
    setError("");
    try {
      const payload = buildUpdatePayload();
      await adminApi.updateUser(userId, payload);
      setSaveMessage("User saved.");
      await loadDetails();
      setEditOpen(false);
    } catch (err) {
      const msg =
        err?.message ||
        err?.response?.data?.error ||
        "Failed to update user.";
      setError(msg);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleResetForm = () => {
    setEditForm(profileToEditForm(details?.profile || null));
    setSaveMessage("");
  };

  return (
    <div>
      <div
        className="row"
        style={{ justifyContent: "space-between", marginBottom: 12 }}
      >
        <h2>User Details</h2>
        <div className="row">
          <button type="button" onClick={() => setEditOpen((o) => !o)}>
            {editOpen ? "Hide editor" : "Edit user"}
          </button>
          <button onClick={() => navigate("/users")}>Back to Users</button>
        </div>
      </div>

      {error ? <div className="error">{error}</div> : null}
      {saveMessage ? <div className="muted" style={{ color: "#0a7" }}>{saveMessage}</div> : null}
      {loading ? <div className="muted">Loading user details...</div> : null}
      {!loading && !error && !details ? (
        <div className="muted">No data found.</div>
      ) : null}

      {!loading && details ? (
        <>
          {editOpen ? (
            <div className="card" style={{ marginBottom: 16 }}>
              <h3>Edit user (full profile)</h3>
              <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
                Changes apply immediately. Clearing 2FA only happens when{" "}
                <strong>2FA enabled</strong> is turned off — or use the OTP clear
                flag. Wallet totals are persisted on the wallet record.
              </p>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: "10px 16px",
                }}
              >
                <label style={{ gridColumn: "1 / -1" }}>
                  <span>Email</span>
                  <input
                    value={editForm.email}
                    onChange={(ev) => setField("email", ev.target.value)}
                    style={{ width: "100%" }}
                  />
                </label>
                <label style={{ gridColumn: "1 / -1" }}>
                  <span>Name</span>
                  <input
                    value={editForm.name}
                    onChange={(ev) => setField("name", ev.target.value)}
                    style={{ width: "100%" }}
                  />
                </label>
                <label>
                  <span>Phone</span>
                  <input
                    value={editForm.phone}
                    onChange={(ev) => setField("phone", ev.target.value)}
                    style={{ width: "100%" }}
                  />
                </label>
                <label>
                  <span>Referral code</span>
                  <input
                    value={editForm.referralCode}
                    onChange={(ev) =>
                      setField("referralCode", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>
                <label>
                  <span>Role</span>
                  <select
                    value={editForm.role}
                    onChange={(ev) => setField("role", ev.target.value)}
                    style={{ width: "100%" }}
                  >
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                  </select>
                </label>
                <label style={{ alignSelf: "end" }}>
                  <input
                    type="checkbox"
                    checked={editForm.isActive}
                    onChange={(ev) => setField("isActive", ev.target.checked)}
                  />{" "}
                  Active account
                </label>
                <label style={{ alignSelf: "end" }}>
                  <input
                    type="checkbox"
                    checked={editForm.isEmailVerified}
                    onChange={(ev) =>
                      setField("isEmailVerified", ev.target.checked)
                    }
                  />{" "}
                  Email verified
                </label>
                <label style={{ alignSelf: "end" }}>
                  <input
                    type="checkbox"
                    checked={editForm.twoFAEnabled}
                    onChange={(ev) =>
                      setField("twoFAEnabled", ev.target.checked)
                    }
                  />{" "}
                  2FA enabled
                </label>
                <label style={{ alignSelf: "end" }}>
                  <input
                    type="checkbox"
                    checked={editForm.clearPendingOtp}
                    onChange={(ev) =>
                      setField("clearPendingOtp", ev.target.checked)
                    }
                  />{" "}
                  Clear pending email / OTP challenge
                </label>

                <label style={{ gridColumn: "1 / -1" }}>
                  <span>Address</span>
                  <input
                    value={editForm.address}
                    onChange={(ev) => setField("address", ev.target.value)}
                    style={{ width: "100%" }}
                  />
                </label>
                <label style={{ gridColumn: "1 / -1" }}>
                  <span>Avatar URL</span>
                  <input
                    value={editForm.avatar}
                    onChange={(ev) => setField("avatar", ev.target.value)}
                    style={{ width: "100%" }}
                  />
                </label>

                <h4 style={{ gridColumn: "1 / -1", margin: "16px 0 4px" }}>
                  Nominee
                </h4>
                <label>
                  <span>Nominee name</span>
                  <input
                    value={editForm.nomineeName}
                    onChange={(ev) =>
                      setField("nomineeName", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>
                <label>
                  <span>Nominee email</span>
                  <input
                    value={editForm.nomineeEmail}
                    onChange={(ev) =>
                      setField("nomineeEmail", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>
                <label>
                  <span>Nominee phone</span>
                  <input
                    value={editForm.nomineePhone}
                    onChange={(ev) =>
                      setField("nomineePhone", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>
                <label style={{ gridColumn: "1 / -1" }}>
                  <span>Nominee address</span>
                  <input
                    value={editForm.nomineeAddress}
                    onChange={(ev) =>
                      setField("nomineeAddress", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>

                <h4 style={{ gridColumn: "1 / -1", margin: "16px 0 4px" }}>
                  Withdrawal prefs
                </h4>
                <label>
                  <span>Wallet label</span>
                  <input
                    value={editForm.walletName}
                    onChange={(ev) =>
                      setField("walletName", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>
                <label>
                  <span>Withdrawal method</span>
                  <input
                    value={editForm.withdrawalMethod}
                    onChange={(ev) =>
                      setField("withdrawalMethod", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>
                <label style={{ gridColumn: "1 / -1" }}>
                  <span>Destination address</span>
                  <input
                    value={editForm.destinationAddress}
                    onChange={(ev) =>
                      setField("destinationAddress", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>

                <h4 style={{ gridColumn: "1 / -1", margin: "16px 0 4px" }}>
                  Ledger-style totals (User record)
                </h4>
                <label>
                  <span>Total earnings</span>
                  <input
                    value={editForm.totalEarnings}
                    onChange={(ev) =>
                      setField("totalEarnings", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>
                <label>
                  <span>Total working income</span>
                  <input
                    value={editForm.totalWorkingIncome}
                    onChange={(ev) =>
                      setField("totalWorkingIncome", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>
                <label>
                  <span>Total non-working income</span>
                  <input
                    value={editForm.totalNonWorkingIncome}
                    onChange={(ev) =>
                      setField("totalNonWorkingIncome", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>
                <label>
                  <span>Initial investment level</span>
                  <input
                    value={editForm.initialInvestmentLevel}
                    onChange={(ev) =>
                      setField("initialInvestmentLevel", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>

                <h4 style={{ gridColumn: "1 / -1", margin: "16px 0 4px" }}>
                  Binary placement
                </h4>
                <label style={{ gridColumn: "1 / -1" }}>
                  <span>Binary parent user ID</span>
                  <input
                    value={editForm.binaryParentId}
                    onChange={(ev) =>
                      setField("binaryParentId", ev.target.value)
                    }
                    style={{ width: "100%", fontFamily: "monospace" }}
                  />
                </label>
                <label>
                  <span>Binary side</span>
                  <select
                    value={editForm.binarySide}
                    onChange={(ev) => setField("binarySide", ev.target.value)}
                    style={{ width: "100%" }}
                  >
                    <option value="">(none)</option>
                    <option value="LEFT">LEFT</option>
                    <option value="RIGHT">RIGHT</option>
                  </select>
                </label>

                <h4 style={{ gridColumn: "1 / -1", margin: "16px 0 4px" }}>
                  2FA lockout (optional)
                </h4>
                <label>
                  <span>Failed attempts count</span>
                  <input
                    type="number"
                    min={0}
                    value={editForm.twoFAFailedAttempts}
                    onChange={(ev) =>
                      setField("twoFAFailedAttempts", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>
                <label>
                  <span>Locked until (local datetime)</span>
                  <input
                    type="datetime-local"
                    value={editForm.twoFALockedUntil}
                    onChange={(ev) =>
                      setField("twoFALockedUntil", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>

                <h4 style={{ gridColumn: "1 / -1", margin: "16px 0 4px" }}>
                  Wallet balances
                </h4>
                <label>
                  <span>Total balance</span>
                  <input
                    value={editForm.walletBalance}
                    onChange={(ev) =>
                      setField("walletBalance", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>
                <label>
                  <span>Main</span>
                  <input
                    value={editForm.walletMain}
                    onChange={(ev) => setField("walletMain", ev.target.value)}
                    style={{ width: "100%" }}
                  />
                </label>
                <label>
                  <span>Profit</span>
                  <input
                    value={editForm.walletProfit}
                    onChange={(ev) =>
                      setField("walletProfit", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>
                <label>
                  <span>Reward</span>
                  <input
                    value={editForm.walletReward}
                    onChange={(ev) =>
                      setField("walletReward", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>

                <h4 style={{ gridColumn: "1 / -1", margin: "16px 0 4px" }}>
                  Security
                </h4>
                <label style={{ gridColumn: "1 / -1" }}>
                  <span>New password (leave blank to keep current)</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={editForm.newPassword}
                    onChange={(ev) =>
                      setField("newPassword", ev.target.value)
                    }
                    style={{ width: "100%" }}
                  />
                </label>
              </div>

              <div className="row" style={{ marginTop: 16 }}>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={saveLoading}
                  onClick={handleSave}
                >
                  {saveLoading ? "Saving..." : "Save all changes"}
                </button>
                <button type="button" disabled={saveLoading} onClick={handleResetForm}>
                  Reset form
                </button>
              </div>
            </div>
          ) : null}

          <div className="card" style={{ marginBottom: 12 }}>
            <h3>User Data Snapshot</h3>
            <div className="bar-chart-grid">
              {[
                {
                  label: "Deposits",
                  value: Number(details.summary?.totalDeposits || 0),
                },
                {
                  label: "Withdrawals",
                  value: Number(details.summary?.totalWithdrawals || 0),
                },
                {
                  label: "Earnings",
                  value: Number(details.summary?.totalEarnings || 0),
                },
                {
                  label: "Trade Profit",
                  value: Number(details.summary?.totalProfit || 0),
                },
              ].map((item, _, all) => {
                const maxValue = Math.max(...all.map((v) => v.value), 1);
                const widthPercent = Math.max(8, (item.value / maxValue) * 100);
                return (
                  <div key={item.label} className="bar-row">
                    <div className="bar-label">{item.label}</div>
                    <div className="bar-track">
                      <div
                        className="bar-fill"
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>
                    <div className="bar-value">{item.value.toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="row" style={{ marginBottom: 12 }}>
            <div className="card" style={{ flex: 1 }}>
              <h3>Profile</h3>
              <p>
                User ID:{" "}
                <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
                  {details.profile?.userNumber || "—"}
                </span>
              </p>
              <p>Email: {details.profile?.email || "-"}</p>
              <p>Name: {details.profile?.name || "-"}</p>
              <p>Phone: {details.profile?.phone || "-"}</p>
              <p>Referral: {details.profile?.referralCode || "-"}</p>
              <p>Role: {details.profile?.role || "-"}</p>
              <p>
                Status: {details.profile?.isActive ? "active" : "inactive"}
              </p>
              <p>
                Email verified: {details.profile?.isEmailVerified ? "yes" : "no"}
              </p>
              <p>
                2FA:{" "}
                {details.profile?.twoFAEnabled ? "enabled" : "disabled"}
              </p>
              <p>Nominee: {details.profile?.nomineeName || "-"}</p>
            </div>
            <div className="card" style={{ flex: 1 }}>
              <h3>Summary</h3>
              <p>
                Wallet Balance:{" "}
                {(details.summary?.walletBalance || 0).toFixed(2)} USDT
              </p>
              <p>
                Total Deposits:{" "}
                {(details.summary?.totalDeposits || 0).toFixed(2)} USDT
              </p>
              <p>
                Total Withdrawals:{" "}
                {(details.summary?.totalWithdrawals || 0).toFixed(2)} USDT
              </p>
              <p>
                Total Earnings:{" "}
                {(details.summary?.totalEarnings || 0).toFixed(2)} USDT
              </p>
              <p>Total Trades: {details.summary?.totalTrades || 0}</p>
              <p>Team Size: {details.summary?.teamCount || 0}</p>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 12 }}>
            <h3>Transactions</h3>
            <div className="row" style={{ marginBottom: 10 }}>
              <input
                placeholder="Filter type"
                value={txFilters.type}
                onChange={(e) =>
                  setTxFilters((prev) => ({ ...prev, type: e.target.value }))
                }
              />
              <input
                placeholder="Filter status"
                value={txFilters.status}
                onChange={(e) =>
                  setTxFilters((prev) => ({
                    ...prev,
                    status: e.target.value,
                  }))
                }
              />
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(details.transactions?.items || [])
                    .filter((txn) => {
                      const typeOk = txFilters.type
                        ? String(txn.type || "")
                            .toLowerCase()
                            .includes(txFilters.type.toLowerCase())
                        : true;
                      const statusOk = txFilters.status
                        ? String(txn.status || "")
                            .toLowerCase()
                            .includes(txFilters.status.toLowerCase())
                        : true;
                      return typeOk && statusOk;
                    })
                    .map((txn) => (
                      <tr key={txn.id}>
                        <td>{new Date(txn.createdAt).toLocaleString()}</td>
                        <td>{txn.type || "-"}</td>
                        <td>{txn.status || "-"}</td>
                        <td>{Number(txn.amount || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={details.transactions?.page || page}
              pageSize={details.transactions?.pageSize || pageSize}
              total={details.transactions?.total || 0}
              onPageChange={(nextPage) => setPage(nextPage)}
            />
          </div>

          <div className="row">
            <div className="card" style={{ flex: 1 }}>
              <h3>Recent Withdrawals</h3>
              {(details.withdrawals || []).slice(0, 5).map((item) => (
                <p key={item.id}>
                  {item.status}: {Number(item.amount || 0).toFixed(2)} USDT
                </p>
              ))}
            </div>
            <div className="card" style={{ flex: 1 }}>
              <h3>Recent Commissions</h3>
              {(details.commissions || []).slice(0, 5).map((item) => (
                <p key={item.id}>
                  {item.type || "commission"}:{" "}
                  {Number(item.amount || 0).toFixed(2)} USDT
                </p>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
